import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "./store";
import { getOrgId } from "./orgHelpers";
import { openToast } from "./ToastSlice";
import { PaymentsService } from "../services/PaymentsService";
import { SupplierReceiptsService } from "../services/SupplierReceiptsService";
import { IRepasse, RepasseForm } from "../models/Repasse";

interface RepassesState {
  list: IRepasse[] | null;
  modalAddStatus: boolean;
  creating: boolean;
  loadingResults: boolean;
}

const initialState: RepassesState = {
  list: null,
  modalAddStatus: false,
  creating: false,
  loadingResults: false,
};

const slice = createSlice({
  name: "repasses",
  initialState,
  reducers: {
    setList(s, a: PayloadAction<IRepasse[]>) { s.list = a.payload; },
    setModalAddStatus(s, a: PayloadAction<boolean>) { s.modalAddStatus = a.payload; },
    setCreating(s, a: PayloadAction<boolean>) { s.creating = a.payload; },
    setLoadingResults(s, a: PayloadAction<boolean>) { s.loadingResults = a.payload; },
  },
});

export const { setList, setModalAddStatus, setCreating, setLoadingResults } = slice.actions;
export default slice.reducer;

// "dd/mm/yyyy, hh:mm:ss" (toLocaleString aplicado pelo Api) -> timestamp p/ ordenar.
const ts = (s?: string): number => {
  const m = s?.match(/(\d{2})\/(\d{2})\/(\d{4}).*?(\d{2}):(\d{2}):(\d{2})/);
  if (!m) return 0;
  return new Date(+m[3], +m[2] - 1, +m[1], +m[4], +m[5], +m[6]).getTime();
};

// Busca pagamentos (envios) + recebimentos em paralelo, normaliza p/ IRepasse e
// mescla por data (desc).
export const fetchRepasses = (): AppThunk<Promise<void>> => async (dispatch, getState) => {
  dispatch(setLoadingResults(true));
  try {
    const orgId = getOrgId(getState());
    const [payments, receipts] = await Promise.all([
      PaymentsService.getByOrganization(orgId).catch(() => []),
      SupplierReceiptsService.getByOrganization(orgId).catch(() => []),
    ]);

    const envios: IRepasse[] = (payments ?? []).map((p: any) => ({
      id: p.id, category: "envio", supplier: p.target, asset: p.asset,
      value: p.value, status: p.status, date: p.createdAt,
    }));
    const recebimentos: IRepasse[] = (receipts ?? []).map((r: any) => ({
      id: r.id, category: "recebimento", supplier: r.supplier, asset: r.asset,
      value: r.value, status: r.status, date: r.createdAt,
    }));

    dispatch(setList([...envios, ...recebimentos].sort((a, b) => ts(b.date) - ts(a.date))));
  } catch (e) {
    console.error("fetchRepasses error", e);
  } finally {
    dispatch(setLoadingResults(false));
  }
};

// Cria o repasse no endpoint certo conforme o tipo.
export const createRepasse = (form: RepasseForm): AppThunk<Promise<void>> => async (dispatch, getState) => {
  dispatch(setCreating(true));
  try {
    const orgId = getOrgId(getState());
    if (form.category === "envio") {
      await PaymentsService.create({
        organization_id:    orgId,
        target_supplier_id: Number(form.supplier_id),
        asset_id:           Number(form.asset_id),
        value:              Number(form.value),
      });
    } else {
      await SupplierReceiptsService.create({
        organization_id: orgId,
        supplier_id:     Number(form.supplier_id),
        asset_id:        Number(form.asset_id),
        value:           String(form.value),
      });
    }
    dispatch(openToast({ title: "Repasse", message: "Repasse registrado com sucesso.", effect: "success" }));
    dispatch(setModalAddStatus(false));
    dispatch(fetchRepasses());
  } catch (e: any) {
    const msg = Array.isArray(e?.message) ? e.message.join(" · ") : (e?.message ?? "Falha ao registrar o repasse.");
    dispatch(openToast({ title: "Erro ao registrar repasse", message: msg, effect: "error" }));
    console.error("createRepasse error", e);
  } finally {
    dispatch(setCreating(false));
  }
};
